// import React, { useState, useEffect, useRef } from 'react';
// import { Box, useTheme } from '@mui/material';

// interface Props {
//     isListening: boolean;
// }

// const AudioMeter: React.FC<Props> = ({ isListening }) => {
//     const [level, setLevel] = useState(0);
//     const contextRef = useRef<AudioContext | null>(null);
//     const analyserRef = useRef<AnalyserNode | null>(null);
//     const streamRef = useRef<MediaStream | null>(null);
//     const animationRef = useRef<number | null>(null);
//     const theme = useTheme();

//     useEffect(() => {
//         if (!isListening) {
//             if (contextRef.current) {
//                 contextRef.current.close();
//                 contextRef.current = null;
//             }
//             if (streamRef.current) {
//                 streamRef.current.getTracks().forEach((track) => track.stop());
//                 streamRef.current = null;
//             }
//             if (animationRef.current) {
//                 cancelAnimationFrame(animationRef.current);
//             }
//             setLevel(0);
//             return;
//         }

//         const setupAnalysis = async () => {
//             try {
//                 if (streamRef.current) {
//                 } else {
//                     const stream = await navigator.mediaDevices.getUserMedia({
//                         audio: true,
//                     });
//                     streamRef.current = stream;
//                 }

//                 if (contextRef.current) {
//                     contextRef.current.close();
//                 }

//                 const context = new AudioContext();
//                 contextRef.current = context;

//                 const analyser = context.createAnalyser();
//                 analyserRef.current = analyser;

//                 const source = context.createMediaStreamSource(streamRef.current);
//                 source.connect(analyser);

//                 analyser.fftSize = 256;
//                 analyser.smoothingTimeConstant = 0.8;
//                 const bufferLength = analyser.frequencyBinCount;
//                 const dataArray = new Uint8Array(bufferLength);

//                 const updateLevel = () => {
//                     if (!analyserRef.current) return;

//                     analyser.getByteFrequencyData(dataArray);

//                     let sum = 0;
//                     for (let i = 0; i < bufferLength; i++) {
//                         sum += dataArray[i] * dataArray[i];
//                     }
//                     const rms = Math.sqrt(sum / bufferLength);
//                     const normalized = Math.min(Math.max(rms / 100, 0), 1);

//                     setLevel(normalized);
//                     animationRef.current = requestAnimationFrame(updateLevel);
//                 };

//                 updateLevel();
//             } catch (error) {
//                 console.error('AudioMeter - Error accessing microphone:', error);

//                 if (error.name === 'NotAllowedError') {
//                     console.error('AudioMeter - Microphone permission denied');
//                 } else if (error.name === 'NotFoundError') {
//                     console.error('AudioMeter - No microphone found');
//                 } else if (error.name === 'NotReadableError') {
//                     console.error('AudioMeter - Microphone already in use');
//                 } else {
//                     console.error('AudioMeter - Unknown microphone error:', error);
//                 }
//             }
//         };

//         setupAnalysis();

//         return () => {
//             if (contextRef.current) {
//                 contextRef.current.close();
//                 contextRef.current = null;
//             }
//             if (streamRef.current) {
//                 streamRef.current.getTracks().forEach((track) => track.stop());
//                 streamRef.current = null;
//             }
//             if (animationRef.current) {
//                 cancelAnimationFrame(animationRef.current);
//             }
//         };
//     }, [isListening]);

//     const ratios = [1, 0.5, 0.7, 1, 0.7, 0.5];
//     const maxHeight = 40;

//     return (
//         <>
//             {isListening && (
//                 <Box
//                     sx={{
//                         display: 'flex',
//                         justifyContent: 'center',
//                         alignItems: 'center',
//                         height: '40px',
//                         width: 'auto',
//                         minWidth: '29px',
//                         gap: '2px',
//                     }}
//                 >
//                     {ratios.map((ratio, index) => {
//                         const height = level > 0 ? Math.max(level * maxHeight * ratio, 6) : 4;
//                         return (
//                             <Box
//                                 key={index}
//                                 sx={{
//                                     width: '4px',
//                                     height: `${height}px`,
//                                     backgroundColor: theme.palette.action.disabled,
//                                     borderRadius: level > 0 ? '2px' : '50%',
//                                     transition: 'height 0.03s ease, border-radius 0.03s ease',
//                                     transform: 'translateY(0)',
//                                 }}
//                             />
//                         );
//                     })}
//                 </Box>
//             )}
//         </>
//     );
// };

// export default AudioMeter;

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Box, useTheme } from '@mui/material';

interface Props {
    isListening: boolean;
}

const AudioMeter: React.FC<Props> = ({ isListening }) => {
    const [level, setLevel] = useState(0);
    const contextRef = useRef<AudioContext | null>(null);
    const analyserRef = useRef<AnalyserNode | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const animationRef = useRef<number | null>(null);
    const theme = useTheme();

    const barHeights = useMemo(() => {
        const ratios = [1, 0.5, 0.7, 1, 0.7, 0.5];
        const maxHeight = 40;

        return ratios.map(ratio => {
            const height = level > 0 ? Math.max(level * maxHeight * ratio, 6) : 4;
            return height;
        });
    }, [level]);

    useEffect(() => {
        if (!isListening) {
            if (contextRef.current && contextRef.current.state !== 'closed') {
                contextRef.current.close();
                contextRef.current = null;
            }
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((track) => track.stop());
                streamRef.current = null;
            }
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
                animationRef.current = null;
            }
            setLevel(0);
            return;
        }

        const setupAnalysis = async () => {
            try {
                if (!streamRef.current) {
                    const stream = await navigator.mediaDevices.getUserMedia({
                        audio: {
                            echoCancellation: false,
                            noiseSuppression: false,
                            autoGainControl: false
                        }
                    });
                    streamRef.current = stream;
                }

                if (contextRef.current && contextRef.current.state !== 'closed') {
                    contextRef.current.close();
                }

                const context = new AudioContext();
                contextRef.current = context;

                if (context.state === 'suspended') {
                    await context.resume();
                }

                const analyser = context.createAnalyser();
                analyserRef.current = analyser;

                analyser.fftSize = 1024;

                analyser.smoothingTimeConstant = 0.3;

                const source = context.createMediaStreamSource(streamRef.current);
                source.connect(analyser);

                const bufferLength = analyser.frequencyBinCount;
                const dataArray = new Uint8Array(bufferLength);

                const sampleRate = context.sampleRate;
                const nyquist = sampleRate / 2;
                const binWidth = nyquist / bufferLength;

                const voiceStartBin = Math.floor(85 / binWidth);
                const voiceEndBin = Math.floor(1000 / binWidth);

                const updateLevel = () => {
                    if (!analyserRef.current || !contextRef.current || contextRef.current.state === 'closed') {
                        return;
                    }

                    analyser.getByteFrequencyData(dataArray);

                    let sum = 0;
                    let count = 0;
                    for (let i = voiceStartBin; i < Math.min(voiceEndBin, bufferLength); i++) {
                        sum += dataArray[i] * dataArray[i];
                        count++;
                    }

                    if (count === 0) {
                        setLevel(0);
                        animationRef.current = requestAnimationFrame(updateLevel);
                        return;
                    }

                    const rms = Math.sqrt(sum / count);

                    const dbValue = 20 * Math.log10(Math.max(rms, 1) / 255);
                    const normalizedDb = Math.max(0, (dbValue + 60) / 60);
                    const normalized = Math.min(normalizedDb, 1);

                    setLevel(normalized);
                    animationRef.current = requestAnimationFrame(updateLevel);
                };

                updateLevel();
            } catch (error) {
                console.error('AudioMeter - Error accessing microphone:', error);

                if (error instanceof Error) {
                    if (error.name === 'NotAllowedError') {
                        console.error('AudioMeter - Microphone permission denied');
                    } else if (error.name === 'NotFoundError') {
                        console.error('AudioMeter - No microphone found');
                    } else if (error.name === 'NotReadableError') {
                        console.error('AudioMeter - Microphone already in use');
                    } else {
                        console.error('AudioMeter - Unknown microphone error:', error);
                    }
                }
                setLevel(0);
            }
        };

        setupAnalysis();

        return () => {
            if (contextRef.current && contextRef.current.state !== 'closed') {
                contextRef.current.close();
                contextRef.current = null;
            }
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((track) => track.stop());
                streamRef.current = null;
            }
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
                animationRef.current = null;
            }
        };
    }, [isListening]);

    const ratios = [1, 0.5, 0.7, 1, 0.7, 0.5];

    return (
        <>
            {isListening && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '40px',
                        width: 'auto',
                        minWidth: '29px',
                        gap: '2px',
                    }}
                >
                    {ratios.map((ratio, index) => {
                        const height = barHeights[index];
                        return (
                            <Box
                                key={index}
                                sx={{
                                    width: '4px',
                                    height: `${height}px`,
                                    backgroundColor: theme.palette.action.disabled,
                                    borderRadius: level > 0 ? '2px' : '50%',
                                    transform: 'translateY(0)',
                                }}
                            />
                        );
                    })}
                </Box>
            )}
        </>
    );
};

export default AudioMeter;